import { useTheme } from '../../hooks/useTheme';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { FormProvider, Controller } from 'react-hook-form';
import { Tile, Button, Card, Pill, 
        Input, TextArea, Select, MenuItem } from '@dsi/react-eds';
import { useEiapClusterForm } from './useEiapClusterForm';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { EiapClusterFormData, EiapClusterData, EnvironmentCategory, EndpointFormData, EndpointsType, EiapEndpoint } from './EiapClusterInterfaces';
import { useLoader } from '../../hooks/useLoader';
import api from '../../api/axiosInstance';
import { AxiosError } from 'axios';
import styles from './AddEditEiapCluster.module.css';
import AddEditEndpointDialog from './AddEditEndpointDialog';
import EiapClusterEnmList from './EiapClusterEnmList';

const AddEditEiapCluster = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { clusterId } = useParams<{ clusterId?: string }>();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();
  const [addedEndpoints, setAddedEndpoints] = useState({
    rlo: false,
    eic: false,
    eolm: false,
  });
  const [selectedEndpoint, setSelectedEndpoint] = useState<EndpointFormData>();
  const [selectedEndpointType, setSelectedEndpointType] = useState<string>('RLO');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [existingCluster, setExistingCluster] = useState<EiapClusterData | null>(null);
  const [linkedEnms, setLinkedEnms] = useState<EiapEndpoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const form = useEiapClusterForm();

  // Fetch existing cluster data if editing
  useEffect(() => {
    if (clusterId) {
      fetchClusterData(clusterId);
    } else {
      setIsLoading(false);
    }
  }, [clusterId]);

  const fetchClusterData = async (id: string) => {
    try {
      showLoader();
      const response = await api.get(`/api/eiap-cluster/${id}`);
      const cluster = response.data;
      setExistingCluster(cluster);

      // Set form values with fetched data
      form.reset({
        name: cluster.name,
        category: cluster.category,
        description: cluster.description,
        rloEndpoint: {
          url: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'RLO')?.url || '',
          user: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'RLO')?.user || '',
          password: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'RLO')?.password || '',
          tlsCertificate: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'RLO')?.tlsCertificate || '',
        },
        eicEndpoint: {
          url: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'EIC')?.url || '',
          clientId: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'EIC')?.clientId || '',
          clientSecret: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'EIC')?.clientSecret || '',
          user: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'EIC')?.user || '',
          password: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'EIC')?.password || '',
          tlsCertificate: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'EIC')?.tlsCertificate || '',
        },
        eolmEndpoint: {
          url: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'EOLM')?.url || '',
          user: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'EOLM')?.user || '',
          password: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'EOLM')?.password || '',
          tlsCertificate: cluster.endpoints.find((e: any) => e.targetEnvToolType?.name === 'EOLM')?.tlsCertificate || '',
        },
        linkedEnms: cluster.endpoints.filter((e: EiapEndpoint) => e.targetEnvToolType?.name === 'ENM') || [],
      });

      setLinkedEnms(cluster.endpoints.filter((e: EiapEndpoint) => e.targetEnvToolType?.name === 'ENM'));

      // Initialize endpoints state based on existing data
      const hasRlo = cluster.endpoints.some((e: any) => e.targetEnvToolType?.name === 'RLO');
      const hasEic = cluster.endpoints.some((e: any) => e.targetEnvToolType?.name === 'EIC');
      const hasEolm = cluster.endpoints.some((e: any) => e.targetEnvToolType?.name === 'EOLM');
      setAddedEndpoints({ rlo: hasRlo, eic: hasEic, eolm: hasEolm });

      setIsLoading(false);
      hideLoader();
    } catch (error) {
      hideLoader();
      setIsLoading(false);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching cluster data.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
      // Navigate back if cluster not found
      navigate('/settings/eiap-clusters');
    }
  };

  const categoryOptions: EnvironmentCategory[] = ['Production', 'Staging', 'Development', 'Testing'];

  const convertFormDataToClusterData = (data: EiapClusterFormData): EiapClusterData => {
    const endpoints = [];
    
    if (addedEndpoints.rlo && data.rloEndpoint.url) {
      endpoints.push({
        targetEnvToolTypeName: 'RLO',
        url: data.rloEndpoint.url,
        clientId: data.eicEndpoint.clientId,
        clientSecret: data.eicEndpoint.clientSecret,
        tlsCertificate: data.rloEndpoint.tlsCertificate,
        enabled: true,
      });
    }
    
    if (addedEndpoints.eic && data.eicEndpoint.url) {
      endpoints.push({
        targetEnvToolTypeName: 'EIC',
        url: data.eicEndpoint.url,
        clientId: data.eicEndpoint.clientId,
        clientSecret: data.eicEndpoint.clientSecret,
        user: data.eicEndpoint.user,
        password: data.eicEndpoint.password,
        tlsCertificate: data.eicEndpoint.tlsCertificate,
        enabled: true,
      });
    }
    
    if (addedEndpoints.eolm && data.eolmEndpoint.url) {
      endpoints.push({
        targetEnvToolTypeName: 'EOLM',
        url: data.eolmEndpoint.url,
        user: data.eolmEndpoint.user,
        password: data.eolmEndpoint.password,
        tlsCertificate: data.eolmEndpoint.tlsCertificate,
        enabled: true,
      });
    }
    
    return {
      id: existingCluster?.id || undefined,
      name: data.name,
      category: data.category,
      description: data.description || '',
      endpoints: endpoints as any,
      status: 'online',
      linkedEnms: linkedEnms.map(le => le.id)
    };
  };

  const saveData = async (data: EiapClusterData) => {
    showLoader();
    try {
      // Use the correct backend endpoint
      if (clusterId) {
        // Update existing cluster
        await api.put(`/api/eiap-cluster/${clusterId}`, data, {
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        // Create new cluster
        await api.post('/api/eiap-cluster', data, {
          headers: { 'Content-Type': 'application/json' },
        });
      }

      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: `EIAP Cluster ${clusterId ? 'updated' : 'created'} successfully!`,
          timestamp: new Date(),
          color: 'green',
        },
      ]);

      hideLoader();
      navigate('/integrations/eiap-clusters');
    } catch (error) {
      console.error('Error while saving EIAP Cluster: ', error);
      hideLoader();
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: 'An unexpected error occurred while saving EIAP Cluster.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
    }
  };

  const handleSave = () => {
    form.handleSubmit(
      (data) => {
        const clusterData = convertFormDataToClusterData(data as unknown as EiapClusterFormData);
        saveData(clusterData);
      },
      (errors) => {
        console.log('FORM ERRORS: ', errors);
        addNotifications([
          {
            title: 'Validation Error',
            icon: 'icon-triangle-warning',
            description: 'Please fix the errors in the form before saving.',
            timestamp: new Date(),
            color: 'red',
          },
        ]);
      }
    )();
  };

  const handleCancel = () => {
    navigate('/integrations/eiap-clusters');
  };

  const renderError = (error: any) => {
    if (!error) return null;
    return (
      <span style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
        <span aria-label="error" role="img">
          ⚠{' '}
        </span>
        {error.message as string}
      </span>
    );
  };

  const addEndpoint = (type: EndpointsType) => {
    setAddedEndpoints(prev => ({ ...prev, [type]: true }));
  };

  const removeEndpoint = (type: EndpointsType) => {
    setAddedEndpoints(prev => ({ ...prev, [type]: false }));
    // Clear form values when removing endpoint
    form.setValue(`${type}Endpoint.url`, '');
    form.setValue(`${type}Endpoint.user`, '');
    form.setValue(`${type}Endpoint.password`, '');
    form.setValue(`${type}Endpoint.clientId`, '');
    form.setValue(`${type}Endpoint.clientSecret`, '');
    form.setValue(`${type}Endpoint.tlsCertificate`, '');
  };

  const selectEndpointForm = (endpointType: string) => {
    let values = form.getValues();
    let selEP: EndpointFormData;
    setSelectedEndpointType(endpointType);
    if (endpointType === 'RLO') {
      selEP = {
        url: values.rloEndpoint.url,
        user: values.rloEndpoint.user,
        password: values.rloEndpoint.password,
        tlsCertificate: values.rloEndpoint.tlsCertificate,
        clientId: '',
        clientSecret: ''
      }
      setSelectedEndpoint(selEP);
    } else if (endpointType === 'EIC') {
      selEP = {
        url: values.eicEndpoint.url,
        user: values.eicEndpoint.user,
        password: values.eicEndpoint.password,
        tlsCertificate: values.eicEndpoint.tlsCertificate,
        clientId: values.eicEndpoint.clientId,
        clientSecret: values.eicEndpoint.clientSecret
      }
      setSelectedEndpoint(selEP);
    } else if (endpointType === 'EOLM') {
      selEP = {
        url: values.eolmEndpoint.url,
        user: values.eolmEndpoint.user,
        password: values.eolmEndpoint.password,
        tlsCertificate: values.eolmEndpoint.tlsCertificate,
        clientId: '',
        clientSecret: ''
      }
      setSelectedEndpoint(selEP);
    }
    setOpenDialog(true);
  }

  const addEndpointForm = (endpoint: EndpointFormData) => {
    const values = form.getValues();
    let newValues;
    let type: EndpointsType = 'rlo';
    if (selectedEndpointType === 'RLO') {
      newValues = {...values, rloEndpoint: {
        url: endpoint.url,
        clientId: endpoint.clientId,
        clientSecret: endpoint.clientSecret,
        tlsCertificate: endpoint.tlsCertificate
      }}
    } else if (selectedEndpointType === 'EIC') {
      newValues = {...values, eicEndpoint: {
        url: endpoint.url,
        user: endpoint.user,
        password: endpoint.password,
        clientId: endpoint.clientId,
        clientSecret: endpoint.clientSecret,
        tlsCertificate: endpoint.tlsCertificate
      }}
      type = 'eic';
    } else if (selectedEndpointType === 'EOLM') {
      newValues = {...values, eolmEndpoint: {
        url: endpoint.url,
        user: endpoint.user,
        password: endpoint.password,
        tlsCertificate: endpoint.tlsCertificate
      }}
      type = 'eolm'; 
    }
    form.reset(newValues);
    addEndpoint(type);
    cancelEndpointForm();
  }

  const cancelEndpointForm = () => {
    setSelectedEndpointType('');
    setSelectedEndpoint(undefined);
    setOpenDialog(false);
  }

  const handleSelectEnms = (enms: EiapEndpoint[]) => {
    setLinkedEnms(enms);
  }

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <p>Loading cluster data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
      {/* Page Header */}
      <div className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark}`}>
        <h2 style={{ marginBottom: '8px' }}>
          {clusterId ? 'Edit EIAP Cluster' : 'Create New EIAP Cluster'}
        </h2>
        <p style={{ color: '#6a6a6a' }}>
          Set up EIAP cluster by defining its information. RLO, EIC and EOLM endpoints, ENMs, and environment asssociation.
        </p>
      </div>

      <FormProvider {...form}>
        <form id="eiap-cluster-form">
          <div className="row">
            {/* Cluster Information Tile */}
            <div className="column sm-12">
              <ReactEDSWrapper component={Tile} title="Cluster Information" id="cluster-info-tile">
                <div className="row form-body">
                  <div className="lg-6 md-12 sm-12">
                    <div className="row">
                      <div className="column lg-6 md-12 sm-12">
                        <div className={styles.formElement}>
                          <label htmlFor="cluster-name" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Cluster Name <span style={{ color: '#008CFF' }}>*</span>
                          </label>
                          <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                              <ReactEDSWrapper
                                component={Input}
                                id="cluster-name"
                                type="text"
                                placeholder="e.g., Production EIAP Cluster"
                                {...field}
                              />
                            )}
                          />
                          {renderError((form.formState.errors as any).name)}
                        </div>
                      </div>
                      <div className="column lg-6 md-12 sm-12">
                        <div className={styles.formElement}>
                          <label htmlFor="cluster-category" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Environment Category <span style={{ color: '#008CFF' }}>*</span>
                          </label>
                          <Controller
                            name="category"
                            control={form.control}
                            render={({ field }) => (
                              <div>
                                <ReactEDSWrapper
                                  component={Select}
                                  className={styles.allWidthSelect}
                                  id="cluster-category"
                                  value={field.value}
                                  onChange={(value: any) => {
                                    const stringValue = Array.isArray(value) ? value[0] : value;
                                    field.onChange(stringValue);
                                  }}
                                >
                                  {categoryOptions.map((cat) => (
                                    <ReactEDSWrapper component={MenuItem} key={cat} value={cat}>
                                      {cat}
                                    </ReactEDSWrapper>
                                  ))}
                                </ReactEDSWrapper>
                              </div>
                            )}
                          />
                          {renderError((form.formState.errors as any).category)}
                        </div>
                      </div>
                      <div className="column lg-12">
                        <div className={styles.formElement}>
                          <label htmlFor="cluster-description" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Description
                          </label>
                          <Controller
                            name="description"
                            control={form.control}
                            render={({ field }) => (
                              <ReactEDSWrapper
                                component={TextArea}
                                id="cluster-description"
                                rows={3}
                                placeholder="Describe the purpose of this EIAP cluster..."
                                {...field}
                              />
                            )}
                          />
                          {renderError((form.formState.errors as any).description)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ReactEDSWrapper>
            </div>

            {/* Endpoints Section */}
            <div className="column sm-12">
              <ReactEDSWrapper component={Tile} title="Endpoints" id="endpoints-tile">
                <p style={{ color: '#6a6a6a' }}>
                  Define the endpoints associated with this cluster, including RLO, EIC and EOLM.
                </p>
                <div className="row form-body">
                  {/* ADD Buttons */}
                  <div className="column sm-12">
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                      {!addedEndpoints.rlo && (
                        <ReactEDSWrapper 
                          component={Button} 
                          variant="secondary"
                          onClick={() => selectEndpointForm('RLO')}
                        >
                          + Add: RLO (RAN Lifecycle Orchestrator)
                        </ReactEDSWrapper>
                      )}
                      {!addedEndpoints.eic && (
                        <ReactEDSWrapper 
                          component={Button} 
                          variant="secondary"
                          onClick={() => selectEndpointForm('EIC')}
                        >
                          + Add: EIC (Ericsson Intelligent Controller)
                        </ReactEDSWrapper>
                      )}
                      {!addedEndpoints.eolm && (
                        <ReactEDSWrapper 
                          component={Button} 
                          variant="secondary"
                          onClick={() => selectEndpointForm('EOLM')}
                        >
                          + Add: EOLM (Ericsson Orchestrator Lifecycle Management)
                        </ReactEDSWrapper>
                      )}
                    </div>
                  </div>

                  <div className='row'>
                    {/* RLO Endpoint Fields */}
                    {addedEndpoints.rlo && (
                      <div className="column sm-12 md-6 xl-4">
                        <ReactEDSWrapper
                          component={Card}
                          className={styles.cardNoMargin}
                          title={'RLO'}
                          subtitle={'RAN Lifecycle Orchestrator'}
                        >

                          {/* URL Badge */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', marginBottom: '12px' }}>
                              <ReactEDSWrapper key={`rlo_url_pill`} component={Pill}>
                                  <i className="icon icon-globe" /> {form.getValues()?.rloEndpoint?.url}
                              </ReactEDSWrapper>
                          </div>
                          {/* URL Badge */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', marginBottom: '12px' }}>
                              <ReactEDSWrapper key={`rlo_user_pill`} component={Pill}>
                                  <i className="icon icon-profile" /> {form.getValues()?.rloEndpoint?.clientId}
                              </ReactEDSWrapper>
                          </div>

                          {/* Action Buttons */}
                          <div>
                              <ReactEDSWrapper
                              component={Button}
                              variant="secondary"
                              onClick={() => selectEndpointForm('RLO')}
                              >
                              <i className="icon icon-edit" /> Edit
                              </ReactEDSWrapper>
                              <ReactEDSWrapper
                              component={Button}
                              variant="secondary"
                              onClick={() => removeEndpoint('rlo')}
                              >
                              <i className="icon icon-trashcan" /> Delete
                              </ReactEDSWrapper>
                          </div>
                        </ReactEDSWrapper>
                      </div>
                    )}

                    {/* EIC Endpoint Fields */}
                    {addedEndpoints.eic && (
                      <div className="column sm-12 md-6 xl-4">
                        <ReactEDSWrapper
                          component={Card}
                          className={styles.cardNoMargin}
                          title={'EIC'}
                          subtitle={'Ericsson Intelligent Controller'}
                        >

                          {/* URL Badge */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', marginBottom: '12px' }}>
                              <ReactEDSWrapper key={`eic_url_pill`} component={Pill}>
                                  <i className="icon icon-globe" /> {form.getValues()?.eicEndpoint?.url}
                              </ReactEDSWrapper>
                          </div>
                          {/* URL Badge */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', marginBottom: '12px' }}>
                              <ReactEDSWrapper key={`eic_user_pill`} component={Pill}>
                                  <i className="icon icon-profile" /> {form.getValues()?.eicEndpoint?.clientId}
                              </ReactEDSWrapper>
                          </div>

                          {/* Action Buttons */}
                          <div>
                              <ReactEDSWrapper
                              component={Button}
                              variant="secondary"
                              onClick={() => selectEndpointForm('EIC')}
                              >
                              <i className="icon icon-edit" /> Edit
                              </ReactEDSWrapper>
                              <ReactEDSWrapper
                              component={Button}
                              variant="secondary"
                              onClick={() => removeEndpoint('eic')}
                              >
                              <i className="icon icon-trashcan" /> Delete
                              </ReactEDSWrapper>
                          </div>
                        </ReactEDSWrapper>
                      </div>
                    )}

                    {/* EOLM Endpoint Fields */}
                    {addedEndpoints.eolm && (
                      <div className="column sm-12 md-6 xl-4">
                        <ReactEDSWrapper
                          component={Card}
                          className={styles.cardNoMargin}
                          title={'EOLM'}
                          subtitle={'Ericsson Orchestrator Lifecycle Manager'}
                        >

                          {/* URL Badge */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', marginBottom: '12px' }}>
                              <ReactEDSWrapper key={`eolm_url_pill`} component={Pill}>
                                  <i className="icon icon-globe" /> {form.getValues()?.eolmEndpoint?.url}
                              </ReactEDSWrapper>
                          </div>
                          {/* URL Badge */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', marginBottom: '12px' }}>
                              <ReactEDSWrapper key={`eolm_user_pill`} component={Pill}>
                                  <i className="icon icon-profile" /> {form.getValues()?.eolmEndpoint?.user}
                              </ReactEDSWrapper>
                          </div>

                          {/* Action Buttons */}
                          <div>
                              <ReactEDSWrapper
                              component={Button}
                              variant="secondary"
                              onClick={() => selectEndpointForm('EOLM')}
                              >
                              <i className="icon icon-edit" /> Edit
                              </ReactEDSWrapper>
                              <ReactEDSWrapper
                              component={Button}
                              variant="secondary"
                              onClick={() => removeEndpoint('eolm')}
                              >
                              <i className="icon icon-trashcan" /> Delete
                              </ReactEDSWrapper>
                          </div>
                        </ReactEDSWrapper>
                      </div>
                    )}
                  </div>
                </div>
              </ReactEDSWrapper>
            </div>

            <div className='column sm-12'>
              <ReactEDSWrapper component={Tile} title="ENMs" id="enms-tile">
                <p style={{ color: '#6a6a6a' }}>
                  Associate available ENMs to this cluster by selecting them in the following table.<br/>
                  Each ENM can only be linked to one cluster. You can also link the ENMs from the ENMs section.
                </p>
                <div>
                  <span className={styles.followTitle}>ENMs List</span>
                </div>
                <EiapClusterEnmList enms={linkedEnms} selectEnms={handleSelectEnms} />
              </ReactEDSWrapper>
            </div>

            {/* Action Buttons */}
            <div className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark} column sm-12`}>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <ReactEDSWrapper component={Button} variant="secondary" onClick={handleCancel}>
                  Cancel
                </ReactEDSWrapper>
                <ReactEDSWrapper component={Button} disabled={!form.formState.isValid} primary onClick={handleSave}>
                  {clusterId ? 'Update' : 'Create'}
                </ReactEDSWrapper>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
      <AddEditEndpointDialog 
        onSave={addEndpointForm} 
        onCancel={cancelEndpointForm} 
        endpoint={selectedEndpoint} 
        endpointType={selectedEndpointType} 
        open={openDialog} 
      />
    </div>
  );
};

export default AddEditEiapCluster;
